import React, { useEffect, useState, useMemo } from "react";
import api, { todayISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { toast } from "sonner";
import { MessageSquareHeart, Save, Pencil, Trash2 } from "lucide-react";

const mondayOf = (d = new Date()) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x.toISOString().slice(0, 10);
};
const weekEndOf = (start) => { const x = new Date(start); x.setDate(x.getDate() + 6); return x.toISOString().slice(0, 10); };

const OT_RATINGS = ["Excellent", "Good", "Average", "Poor"];
const OT_RATING_SCORE = { Excellent: 100, Good: 75, Average: 50, Poor: 25 };
const OT_RATING_FIELDS = [["technical_skill", "Technical Skill"], ["sterilization_hygiene", "Sterilization & Hygiene"], ["teamwork_communication", "Teamwork & Communication"], ["patient_care_safety", "Patient Care & Safety"]];
const otBlank = { ot_head_name: "", date: todayISO(), technician_id: "", technical_skill: "", sterilization_hygiene: "", teamwork_communication: "", patient_care_safety: "", overall_performance: "", error_made: "", improvement_needed: "" };

function OtHeadFeedback({ technicians }) {
  const heads = technicians.filter((t) => /head/i.test(t.role || ""));
  const headOptions = heads.length ? heads : technicians;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(otBlank);
  const [editId, setEditId] = useState(null);
  const [filterTech, setFilterTech] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  const load = () => {
    const params = {};
    if (filterTech !== "all") params.technician_id = filterTech;
    if (filterDate) params.date = filterDate;
    api.get("/ot-head-feedback", { params }).then(({ data }) => setRows(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTech, filterDate]);

  const entryScore = (r) => {
    const vals = [...OT_RATING_FIELDS.map(([k]) => r[k]), r.overall_performance].filter((v) => v in OT_RATING_SCORE).map((v) => OT_RATING_SCORE[v]);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  };

  const submit = async () => {
    if (!form.ot_head_name || !form.technician_id) { toast.error("Select OT Head & Technician"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/ot-head-feedback/${editId}`, form);
      else await api.post("/ot-head-feedback", form);
      toast.success(editId ? "Entry updated" : "OT Head feedback saved");
      setForm(otBlank); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const startEdit = (r) => {
    setForm({ ot_head_name: r.ot_head_name, date: r.date, technician_id: r.technician_id, technical_skill: r.technical_skill || "", sterilization_hygiene: r.sterilization_hygiene || "", teamwork_communication: r.teamwork_communication || "", patient_care_safety: r.patient_care_safety || "", overall_performance: r.overall_performance || "", error_made: r.error_made || "", improvement_needed: r.improvement_needed || "" });
    setEditId(r.id);
  };
  const del = async (r) => {
    if (!window.confirm("Delete this entry? (soft delete — history preserved)")) return;
    await api.delete(`/ot-head-feedback/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="mt-10" data-testid="ot-head-feedback-section">
      <h2 className="font-heading text-2xl font-light mb-4">OT Head Feedback for Technician</h2>
      <Card className="p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-slate-400">OT Head Name</label>
            <select data-testid="oth-head" className={inp} value={form.ot_head_name} onChange={(e) => set("ot_head_name", e.target.value)}>
              <option value="">Select…</option>{headOptions.map((t) => <option key={t.employee_id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-400">Date</label><input data-testid="oth-date" type="date" className={inp} value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div><label className="text-xs text-slate-400">Technician</label>
            <select data-testid="oth-technician" className={inp} value={form.technician_id} onChange={(e) => set("technician_id", e.target.value)}>
              <option value="">Select…</option>{technicians.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.name}</option>)}
            </select>
          </div>
          {OT_RATING_FIELDS.map(([k, label]) => (
            <div key={k}><label className="text-xs text-slate-400">{label}</label>
              <select data-testid={`oth-${k}`} className={inp} value={form[k]} onChange={(e) => set(k, e.target.value)}>
                <option value="">—</option>{OT_RATINGS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          ))}
          <div><label className="text-xs text-slate-400">Overall Performance</label>
            <select data-testid="oth-overall" className={inp} value={form.overall_performance} onChange={(e) => set("overall_performance", e.target.value)}>
              <option value="">—</option>{OT_RATINGS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3"><label className="text-xs text-slate-400">Error Made (optional)</label><input className={inp} value={form.error_made} onChange={(e) => set("error_made", e.target.value)} /></div>
          <div className="sm:col-span-3"><label className="text-xs text-slate-400">Improvement Needed (optional)</label><input className={inp} value={form.improvement_needed} onChange={(e) => set("improvement_needed", e.target.value)} /></div>
        </div>
        <div className="flex gap-2 mt-4">
          <button data-testid="oth-save-btn" onClick={submit} disabled={saving} className="flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all disabled:opacity-50">
            <Save size={16} /> {saving ? "Saving…" : editId ? "Update Entry" : "Save Entry"}
          </button>
          {editId && <button onClick={() => { setForm(otBlank); setEditId(null); }} className="text-sm text-slate-400 hover:text-white px-3">Cancel edit</button>}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-white/10">
          <span className="text-sm text-slate-400">Filter:</span>
          <select data-testid="oth-filter-tech" className={inp + " w-auto"} value={filterTech} onChange={(e) => setFilterTech(e.target.value)}>
            <option value="all">All Technicians</option>{technicians.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.name}</option>)}
          </select>
          <input data-testid="oth-filter-date" type="date" className={inp + " w-auto"} value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
          {filterDate && <button onClick={() => setFilterDate("")} className="text-xs text-slate-400 hover:text-white">Clear date</button>}
        </div>
        {rows.length === 0 ? <Empty text="No OT Head feedback entries." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                  {["Date", "OT Head", "Technician", "Technical", "Sterilization", "Teamwork", "Patient Care", "Overall", "Score", "Error Made", "Improvement Needed", ""].map((h) => <th key={h} className="py-3 px-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const s = entryScore(r);
                  return (
                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/5 align-top" data-testid={`oth-row-${r.id}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.date}</td>
                      <td className="px-3 text-slate-300">{r.ot_head_name}</td>
                      <td className="px-3">{r.technician_name || r.technician_id}</td>
                      {OT_RATING_FIELDS.map(([k]) => <td key={k} className="px-3 text-slate-300 text-xs">{r[k] || "—"}</td>)}
                      <td className="px-3 text-slate-300 text-xs">{r.overall_performance || "—"}</td>
                      <td className={`px-3 font-semibold ${s === null ? "text-slate-600" : s >= 75 ? "text-emerald-400" : s >= 50 ? "text-[#C9A24B]" : "text-[#DC2626]"}`}>{s === null ? "—" : `${s}%`}</td>
                      <td className="px-3 text-xs text-slate-300 min-w-[160px] whitespace-pre-wrap">{r.error_made || "—"}</td>
                      <td className="px-3 text-xs text-slate-300 min-w-[160px] whitespace-pre-wrap">{r.improvement_needed || "—"}</td>
                      <td className="px-3 whitespace-nowrap">
                        <button onClick={() => startEdit(r)} className="text-slate-400 hover:text-[#C9A24B] mr-2" data-testid={`oth-edit-${r.id}`}><Pencil size={15} /></button>
                        <button onClick={() => del(r)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`oth-delete-${r.id}`}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function OwnerFeedback() {
  const { technicians } = useApp();
  const techs = useMemo(() => technicians.filter((t) => t.status === "Active"), [technicians]);
  const [tid, setTid] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const blank = { week_start: mondayOf(), score: 8, strengths: "", improvement: "", observation: "", action_expected: "", next_week_expectation: "", remarks: "" };
  const [f, setF] = useState(blank);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  const load = (id) => { if (!id) { setHistory([]); return; } api.get("/owner-feedback", { params: { technician_id: id } }).then(({ data }) => setHistory(data)); };
  useEffect(() => { load(tid); /* eslint-disable-next-line */ }, [tid]);

  const submit = async () => {
    if (!tid) { toast.error("Select a technician"); return; }
    setSaving(true);
    try {
      await api.post("/owner-feedback", { ...f, technician_id: tid, week_end: weekEndOf(f.week_start) });
      toast.success("Weekly feedback saved");
      setF(blank); load(tid);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const tech = technicians.find((t) => t.employee_id === tid);

  return (
    <div className="animate-fade-up" data-testid="owner-feedback-page">
      <PageHeader title="Owner / Management Weekly Feedback" subtitle="Give each technician individual weekly feedback. History is preserved — never overwritten." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4"><MessageSquareHeart size={18} className="text-[#C9A24B]" /><h3 className="font-heading text-lg">New Weekly Feedback</h3></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400">Technician</label>
              <select data-testid="of-technician" className={inp} value={tid} onChange={(e) => setTid(e.target.value)}>
                <option value="">Select…</option>{techs.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.name} — {t.role}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-slate-400">Week Starting (Mon)</label><input data-testid="of-week" type="date" className={inp} value={f.week_start} onChange={(e) => set("week_start", e.target.value)} /></div>
            <div><label className="text-xs text-slate-400">Feedback Score (1–10)</label>
              <select data-testid="of-score" className={inp} value={f.score} onChange={(e) => set("score", parseInt(e.target.value))}>{[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}</select>
            </div>
            {tech && <div className="flex items-end text-xs text-slate-400">{tech.role} · {tech.branch}</div>}
            <div className="sm:col-span-2"><label className="text-xs text-slate-400">Strengths / Good Work</label><input data-testid="of-strengths" className={inp} value={f.strengths} onChange={(e) => set("strengths", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="text-xs text-slate-400">Improvement Required</label><input className={inp} value={f.improvement} onChange={(e) => set("improvement", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="text-xs text-slate-400">Specific Observation</label><input className={inp} value={f.observation} onChange={(e) => set("observation", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="text-xs text-slate-400">Next Week Expectation</label><input className={inp} value={f.next_week_expectation} onChange={(e) => set("next_week_expectation", e.target.value)} /></div>
          </div>
          <button data-testid="of-save-btn" onClick={submit} disabled={saving} className="mt-4 flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all disabled:opacity-50"><Save size={16} /> {saving ? "Saving…" : "Save Weekly Feedback"}</button>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Feedback History {tech ? `— ${tech.name}` : ""}</h3>
          {!tid ? <Empty text="Select a technician to view their weekly feedback history." /> : history.length === 0 ? <Empty text="No weekly feedback recorded yet." /> : (
            <div className="space-y-3 max-h-[560px] overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="bg-white/5 rounded-md p-3" data-testid={`of-history-${h.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[#C9A24B] font-medium">{h.week_start} → {h.week_end}</span>
                    <span className="text-sm font-bold text-emerald-400">{h.score}/10</span>
                  </div>
                  {h.strengths && <p className="text-xs text-slate-300"><span className="text-slate-500">Strength:</span> {h.strengths}</p>}
                  {h.improvement && <p className="text-xs text-slate-300"><span className="text-slate-500">Improve:</span> {h.improvement}</p>}
                  {h.next_week_expectation && <p className="text-xs text-slate-300"><span className="text-slate-500">Next week:</span> {h.next_week_expectation}</p>}
                  <p className="text-[10px] text-slate-600 mt-1">by {h.by}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <OtHeadFeedback technicians={technicians} />
    </div>
  );
}
